import { useCallback, useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useDropzone } from "react-dropzone";
import { FiUploadCloud, FiFile, FiX } from "react-icons/fi";
import { IoChevronDown } from "react-icons/io5";

// ── helpers ───────────────────────────────────────────────────────────────────
const IMAGE_MIME = /^image\//;
const IMAGE_EXT = /\.(jpe?g|png|gif|webp|svg|avif)(\?.*)?$/i;

const isImageValue = (value) => {
  if (!value) return false;
  if (value instanceof File) return IMAGE_MIME.test(value.type);
  return IMAGE_EXT.test(value); // server path / URL string
};

const formatSize = (bytes) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${(bytes / 1024).toFixed(1)} KB`;

const getFileName = (value) =>
  value instanceof File ? value.name : String(value).split("/").pop();

// Resolve dot-notation path in errors object: "modules.a.b" → errors.modules.a.b
const getError = (errors, path) =>
  path.split(".").reduce((o, k) => o?.[k], errors)?.message;

// Shared input class
const inputCls = (hasError) =>
  `w-full border rounded-md px-3 py-2 text-sm bg-white placeholder:text-[#c0c0c0] focus:outline-none transition ${
    hasError
      ? "border-red-400 focus:border-red-400"
      : "border-[#ccc] focus:border-primary-500"
  }`;

// ── file accept hint ──────────────────────────────────────────────────────────
const MIME_LABEL = {
  "image/jpeg": "JPG",
  "image/png": "PNG",
  "image/gif": "GIF",
  "image/webp": "WEBP",
  "image/svg+xml": "SVG",
  "application/pdf": "PDF",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
  "application/msword": "DOC",
  "application/vnd.ms-excel": "XLS",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
};

const buildHint = (accept, maxSize) => {
  const labels = Object.keys(accept || {})
    .map((m) => MIME_LABEL[m] ?? m.split("/")[1].toUpperCase())
    .join(", ");
  return `${labels} · max ${formatSize(maxSize)}`;
};

// ── default file accept ───────────────────────────────────────────────────────
const DEFAULT_ACCEPT = {
  "image/jpeg": [],
  "image/png": [],
  "application/pdf": [],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [],
};
const DEFAULT_MAX_SIZE = 5 * 1024 * 1024;

// ── FileDropzone (internal) ───────────────────────────────────────────────────
const FileDropzone = ({ value, onChange, error, accept, maxSize }) => {
  const [fileError, setFileError] = useState(null);
  const [objectUrl, setObjectUrl] = useState(null);

  // Object URL lifecycle for newly selected File
  useEffect(() => {
    if (!(value instanceof File) || !IMAGE_MIME.test(value.type)) {
      setObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(value);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  const onDrop = useCallback(
    (accepted) => {
      if (accepted.length > 0) {
        setFileError(null);
        onChange(accepted[0]);
      }
    },
    [onChange]
  );

  const onDropRejected = useCallback(
    (rejections) => {
      const code = rejections[0]?.errors[0]?.code;
      if (code === "file-too-large")
        setFileError(`File is too large. Maximum size is ${formatSize(maxSize)}.`);
      else if (code === "file-invalid-type")
        setFileError("Invalid file type.");
      else setFileError("File rejected. Please try again.");
    },
    [maxSize]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    onDropRejected,
    accept,
    maxFiles: 1,
    maxSize,
  });

  const combinedError = fileError || error;
  const hint = buildHint(accept, maxSize);

  // Determine the image src: object URL for new File, or full URL for saved path
  const resolveStoredSrc = (val) => {
    if (typeof val !== "string" || !isImageValue(val)) return null;
    const base = import.meta.env.VITE_IMAGE_BASE_URL ?? "";
    return val.startsWith("http") ? val : `${base}${val}`;
  };
  const imgSrc = objectUrl ?? resolveStoredSrc(value);

  const renderContent = () => {
    if (isDragReject) {
      return (
        <>
          <FiX className="w-7 h-7 text-red-400" />
          <p className="text-sm font-medium text-red-400 text-center">
            Invalid file type or size
          </p>
          <p className="text-xs text-red-300">{hint}</p>
        </>
      );
    }

    if (value) {
      if (imgSrc) {
        return (
          <div className="flex flex-col items-center gap-1 py-2 px-4">
            <img
              src={imgSrc}
              alt="Preview"
              className="max-h-24 max-w-full object-contain rounded shadow-sm"
            />
            <p className="text-xs text-[#65748a] mt-1">{getFileName(value)}</p>
            {value instanceof File && (
              <p className="text-xs text-[#999]">{formatSize(value.size)}</p>
            )}
          </div>
        );
      }
      return (
        <div className="flex flex-col items-center gap-1 py-2 px-4">
          <FiFile className="w-8 h-8 text-primary-500" />
          <p className="text-sm text-gray-600 text-center">{getFileName(value)}</p>
          {value instanceof File && (
            <p className="text-xs text-[#999]">{formatSize(value.size)}</p>
          )}
        </div>
      );
    }

    return (
      <>
        <FiUploadCloud className="w-7 h-7 text-primary-500" />
        <p className="text-sm font-medium text-[#65748a] text-center">
          Click to upload or drag and drop
        </p>
        <p className="text-xs text-[#65748a]">{hint}</p>
      </>
    );
  };

  return (
    <div>
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg min-h-30 flex flex-col items-center justify-center gap-2 cursor-pointer transition ${
          isDragReject
            ? "border-red-400 bg-red-50"
            : isDragActive
            ? "border-primary-500 bg-primary-50"
            : combinedError
            ? "border-red-400"
            : "border-[#e0e6ef]"
        }`}
      >
        <input {...getInputProps()} />
        {renderContent()}
      </div>
      {combinedError && (
        <p className="text-xs text-red-500 mt-1">{combinedError}</p>
      )}
    </div>
  );
};

// ── FormField ─────────────────────────────────────────────────────────────────
const FormField = ({
  name,
  label,
  type = "text",
  placeholder,
  options = [],   // [{ value, label }] — for select
  rows = 3,       // for textarea
  accept = DEFAULT_ACCEPT,  // for file
  maxSize = DEFAULT_MAX_SIZE, // for file
  className = "",
  ...rest
}) => {
  const { register, control, formState: { errors } } = useFormContext();
  const error = getError(errors, name);

  const renderControl = () => {
    if (type === "file") {
      return (
        <Controller
          control={control}
          name={name}
          render={({ field }) => (
            <div className="relative">
              <FileDropzone
                value={field.value}
                onChange={field.onChange}
                error={error}
                accept={accept}
                maxSize={maxSize}
              />
              {field.value && (
                <button
                  type="button"
                  onClick={() => field.onChange(null)}
                  className="absolute top-2 right-2 p-1 rounded-full bg-white border border-[#ccc] text-gray-500 hover:text-red-500 cursor-pointer"
                >
                  <FiX className="w-3 h-3" />
                </button>
              )}
            </div>
          )}
        />
      );
    }

    if (type === "select") {
      const { onChange: customOnChange, ...restProps } = rest;
      const registered = register(name);
      return (
        <div className="relative">
          <select
            {...registered}
            onChange={(e) => {
              registered.onChange(e);
              customOnChange?.(e);
            }}
            className={`${inputCls(!!error)} appearance-none pr-8 cursor-pointer`}
            {...restProps}
          >
            {placeholder && <option value="">{placeholder}</option>}
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <IoChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none w-4 h-4" />
        </div>
      );
    }

    if (type === "textarea") {
      return (
        <textarea
          {...register(name)}
          rows={rows}
          placeholder={placeholder}
          className={`${inputCls(!!error)} resize-none`}
          {...rest}
        />
      );
    }

    // text | email | tel | url | number | password …
    return (
      <input
        {...register(name)}
        type={type}
        placeholder={placeholder}
        className={inputCls(!!error)}
        {...rest}
      />
    );
  };

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && (
        <label className="text-xs font-medium text-[#666]">{label}</label>
      )}
      {renderControl()}
      {/* file errors are rendered inside FileDropzone */}
      {type !== "file" && error && (
        <p className="text-xs text-red-500 mt-0.5">{error}</p>
      )}
    </div>
  );
};

export default FormField;
